import { toPng } from "html-to-image";
import QRCode from "qrcode";

export interface CaptureOptions {
  /**
   * When provided, the footer grows to add a scannable QR code and the join URL alongside
   * the existing scoracle.cc wordmark — turning the export from a passive screenshot into
   * something a recipient can act on. Opt-in, not a default: the plain export already shows
   * member display names, so adding a live join link is a privacy decision the caller makes
   * deliberately (see buildJoinUrl callers), not something every export should carry silently.
   */
  joinUrl?: string;
}

export async function captureElements(els: HTMLElement[], options: CaptureOptions = {}): Promise<Blob> {
  const filter = (node: Node) => !(node instanceof Element && node.hasAttribute("data-no-export"));
  const dataUrls = await Promise.all(
    els.map(el => toPng(el, { pixelRatio: 2, cacheBust: true, filter }))
  );
  return composeWithBranding(dataUrls, options);
}

async function composeWithBranding(dataUrls: string[], { joinUrl }: CaptureOptions = {}): Promise<Blob> {
  const images = await Promise.all(
    dataUrls.map(url => {
      const img = new Image();
      img.src = url;
      return img.decode().then(() => img);
    })
  );

  const scale = 2; // matches pixelRatio: 2
  const plainFooterH = 28 * scale;
  const qrSize = 48 * scale;
  const inviteFooterH = qrSize + 16 * scale; // QR plus top/bottom breathing room
  const footerH = joinUrl ? inviteFooterH : plainFooterH;
  const gap = 2 * scale;
  const width = Math.max(...images.map(i => i.width));
  const contentH =
    images.reduce((h, i) => h + i.height, 0) + (images.length - 1) * gap;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = contentH + footerH;
  const ctx = canvas.getContext("2d")!;

  const cssVars = getComputedStyle(document.documentElement);
  const bg    = cssVars.getPropertyValue("--color-bg").trim()  || "#fff";
  const muted = cssVars.getPropertyValue("--color-text-dim").trim() || "oklch(0.46 0.025 255)";
  const text  = cssVars.getPropertyValue("--color-text").trim() || "#111";

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, canvas.height);

  let y = 0;
  for (let i = 0; i < images.length; i++) {
    ctx.drawImage(images[i], 0, y);
    y += images[i].height;
    if (i < images.length - 1) y += gap;
  }

  if (joinUrl) {
    // Fixed black-on-white regardless of theme — a low-contrast QR code in dark mode would
    // scan unreliably, which matters more here than matching the surrounding chrome.
    const qrDataUrl = await QRCode.toDataURL(joinUrl, { margin: 1, width: qrSize, color: { dark: "#000000", light: "#ffffff" } });
    const qrImg = new Image();
    qrImg.src = qrDataUrl;
    await qrImg.decode();

    const qrX = 14 * scale;
    const qrY = contentH + (footerH - qrSize) / 2;
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

    const textX = qrX + qrSize + 14 * scale;
    ctx.textAlign = "left";
    ctx.fillStyle = text;
    ctx.font = `${13 * scale}px 'DM Sans', system-ui, sans-serif`;
    ctx.textBaseline = "alphabetic";
    ctx.fillText("Scan or tap to join", textX, contentH + footerH / 2 - 2 * scale);
    ctx.fillStyle = muted;
    ctx.font = `${11 * scale}px 'DM Mono', monospace`;
    ctx.fillText(joinUrl.replace(/^https?:\/\//, ""), textX, contentH + footerH / 2 + 14 * scale);

    ctx.fillStyle = muted;
    ctx.font = `${11 * scale}px 'DM Sans', system-ui, sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("scoracle.cc", width - 14 * scale, contentH + footerH / 2);
  } else {
    ctx.fillStyle = muted;
    ctx.font = `${11 * scale}px 'DM Sans', system-ui, sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("scoracle.cc", width - 14 * scale, contentH + footerH / 2);
  }

  return new Promise(resolve => canvas.toBlob(b => resolve(b!), "image/png"));
}

/** Triggers a browser file download for a blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking immediately can race with the browser starting to read the
  // blob URL for larger files, silently dropping the download with no error.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Copies a PNG blob to the clipboard.
 * Returns false when the Clipboard API is unavailable (Firefox, non-HTTPS).
 */
export async function copyBlobToClipboard(blob: Blob): Promise<boolean> {
  try {
    if (!navigator.clipboard?.write) return false;
    await navigator.clipboard.write([
      new ClipboardItem({ "image/png": blob }),
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Shares a PNG via the OS share sheet when the platform supports file
 * sharing (mobile Safari/Chrome, some desktop browsers). On these platforms
 * `<a download>` often just opens the image instead of saving it, and the
 * Clipboard API frequently throws because the async capture work breaks the
 * user-activation chain — the native share sheet sidesteps both and lets the
 * user save to Photos, copy, or share to any app.
 *
 * Returns true if the share sheet handled it (including user cancellation),
 * false if file sharing isn't supported so the caller should fall back to
 * `copyBlobToClipboard` / `downloadBlob`.
 */
export async function shareImageFile(blob: Blob, filename: string, shareUrl?: string): Promise<boolean> {
  if (!navigator.canShare || !navigator.share) return false;
  const file = new File([blob], filename, { type: "image/png" });
  // text/url alongside files isn't universally honoured (some platforms show only the
  // image), but every share target that does support it now carries the join link as a
  // tappable part of the share itself, not just baked into the picture.
  const shareData = shareUrl ? { files: [file], text: shareUrl } : { files: [file] };
  if (!navigator.canShare(shareData)) return false;
  try {
    await navigator.share(shareData);
  } catch (err) {
    if ((err as DOMException)?.name !== "AbortError") return false;
  }
  return true;
}

/** Sanitise league name for use as a filename. */
export function safeFilename(leagueName: string): string {
  return leagueName.replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
}
